import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AlertCircle, CheckCircle2, Clock, XCircle, FolderKanban, TrendingUp } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { getStatusLabel } from '../lib/utils';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Data states
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allTickets, setAllTickets] = useState([]); // Store raw tickets
  const [projectSummaries, setProjectSummaries] = useState({});
  const [floorsMap, setFloorsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // 1. Fetch main data in parallel
      const [statsRes, projectsRes, ticketsRes] = await Promise.all([
        axiosInstance.get('/dashboard/stats'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/tickets')
      ]);

      const projectsData = projectsRes.data;
      const ticketsData = ticketsRes.data;

      setStats(statsRes.data);
      setProjects(projectsData.slice(0, 5)); // Keep top 5 recent projects
      setAllTickets(ticketsData);

      // 2. OPTIMIZED: Fetch Floors in Parallel (Thay vì vòng lặp for...await tuần tự)
      // Tạo một mảng các Promise để gọi API cùng lúc
      const floorPromises = projectsData.map(project => 
        axiosInstance.get(`/projects/${project.id}/floors`)
          .then(res => res.data)
          .catch(() => []) // Nếu lỗi 1 project thì trả về rỗng, không làm crash app
      );

      const floorsResults = await Promise.all(floorPromises);
      
      // Flatten arrays and create map
      const newFloorsMap = {};
      floorsResults.flat().forEach(floor => {
        newFloorsMap[floor.id] = floor;
      });
      setFloorsMap(newFloorsMap);

      // 3. OPTIMIZED: Calculate Summaries (Client-side calculation)
      // Thay vì gọi API lại cho từng project, ta dùng data 'ticketsRes' đã có để tính toán
      // Cách này tiết kiệm hàng chục request API không cần thiết.
      const summaries = {};
      projectsData.slice(0, 5).forEach(project => {
        const projectTickets = ticketsData.filter(t => t.project_id === project.id);
        summaries[project.id] = {
            new: projectTickets.filter(t => t.status === 'new').length,
            in_progress: projectTickets.filter(t => t.status === 'in_progress').length,
            fixed: projectTickets.filter(t => t.status === 'fixed').length,
            rejected: projectTickets.filter(t => t.status === 'rejected').length,
            closed: projectTickets.filter(t => t.status === 'closed').length,
        };
      });
      setProjectSummaries(summaries);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoize sorted tickets to prevent recalculation on re-renders
  const recentTickets = useMemo(() => {
    return [...allTickets]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [allTickets]);

  // Memoize projects map
  const projectsMap = useMemo(() => {
    const map = {};
    // Reconstruct map from the full projects list if available, or just the recent ones
    // Note: In real app, you might want to store 'allProjects' state if needed
    projects.forEach(p => map[p.id] = p); 
    return map;
  }, [projects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.newIssues'),
      value: stats?.new || 0,
      icon: AlertCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      filter: 'new'
    },
    {
      title: t('dashboard.inProgress'),
      value: stats?.in_progress || 0,
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      filter: 'in_progress'
    },
    {
      title: t('dashboard.fixed'),
      value: stats?.fixed || 0,
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      filter: 'fixed'
    },
    {
      title: t('dashboard.overdue'),
      value: stats?.overdue || 0,
      icon: XCircle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      filter: 'overdue'
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('dashboard.welcome')}, {user?.name}. {t('dashboard.overview')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link 
              key={stat.title} 
              to={`/tickets?status=${stat.filter}`}
              className="block transition-transform hover:scale-105"
              data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-black">{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.title === 'Overdue' ? t('dashboard.needsAttention') : t('dashboard.activeTickets')}
                  </p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Projects and Recent Tickets */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Projects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold">{t('dashboard.recentProjects')}</CardTitle>
            <Link to="/projects">
              <Button variant="ghost" size="sm" data-testid="view-all-projects">
                {t('dashboard.viewAll')}
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-4">
            {projects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderKanban className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('dashboard.noProjects')}</p>
              </div>
            ) : (
              projects.map((project) => {
                const summary = projectSummaries[project.id];
                return (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="block p-4 rounded-lg border border-border hover:border-primary transition-colors"
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{project.name}</h3>
                          <p className="text-sm text-muted-foreground">{project.location}</p>
                          <p className="text-xs font-mono text-muted-foreground mt-1">{project.code}</p>
                        </div>
                        <Badge variant="outline">{project.assigned_users?.length || 0} users</Badge>
                      </div>
                      
                      {/* Ticket Summary by Status */}
                      {summary && (
                        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
                          {(() => {
                            const total = summary.new + summary.in_progress + summary.fixed + summary.rejected + summary.closed;
                            return total > 0 ? (
                              <>
                                {summary.new > 0 && (
                                  <Badge className="bg-red-500 text-white text-xs">
                                    {t('tickets.statuses.new')}: {summary.new}
                                  </Badge>
                                )}
                                {summary.in_progress > 0 && (
                                  <Badge className="bg-yellow-500 text-white text-xs">
                                    {t('tickets.statuses.inProgress')}: {summary.in_progress}
                                  </Badge>
                                )}
                                {summary.fixed > 0 && (
                                  <Badge className="bg-green-500 text-white text-xs">
                                    {t('tickets.statuses.fixed')}: {summary.fixed}
                                  </Badge>
                                )}
                                {summary.rejected > 0 && (
                                  <Badge className="bg-orange-500 text-white text-xs">
                                    {t('tickets.statuses.rejected')}: {summary.rejected}
                                  </Badge>
                                )}
                                {summary.closed > 0 && (
                                  <Badge className="bg-gray-500 text-white text-xs">
                                    {t('tickets.statuses.closed')}: {summary.closed}
                                  </Badge>
                                )}
                                <Badge className="bg-blue-600 text-white text-xs font-semibold">
                                  {t('common.total')}: {total}
                                </Badge>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">{t('dashboard.noTickets')}</span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold">{t('dashboard.recentTickets')}</CardTitle>
            <Link to="/tickets">
              <Button variant="ghost" size="sm" data-testid="view-all-tickets">
                {t('dashboard.viewAll')}
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-4">
            {recentTickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('dashboard.noTickets')}</p>
              </div>
            ) : (
              recentTickets.map((ticket) => {
                // Safely access project name - fallback to finding in full list if needed
                const project = projects.find(p => p.id === ticket.project_id) || {};
                const floor = floorsMap[ticket.floor_id];
                
                return (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="block p-4 rounded-lg border border-border hover:border-primary transition-colors"
                    data-testid={`ticket-card-${ticket.id}`}
                  >
                    <div className="space-y-2">
                      {/* Top section: Project, Floor, Ticket Code */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">{project?.name || 'Loading Project...'}</span>
                        <span>•</span>
                        <span>{floor?.name || '...'}</span>
                        {ticket.ticket_code && (
                          <>
                            <span>•</span>
                            <code className="font-mono text-primary">{ticket.ticket_code}</code>
                          </>
                        )}
                      </div>
                      
                      {/* Main content */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{ticket.title}</h3>
                          <p className="text-sm text-muted-foreground truncate">{ticket.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs capitalize">
                              {ticket.priority}
                            </Badge>
                            <span className="text-xs font-mono text-muted-foreground">
                              {ticket.category}
                            </span>
                          </div>
                        </div>
                        <Badge
                          variant={ticket.status === 'closed' ? 'secondary' : 'default'}
                          className={`shrink-0 ${
                            ticket.status === 'new' ? 'bg-red-500' :
                            ticket.status === 'in_progress' ? 'bg-yellow-500' :
                            ticket.status === 'fixed' ? 'bg-green-500' :
                            ticket.status === 'rejected' ? 'bg-orange-500' : ''
                          }`}
                        >
                          {getStatusLabel(ticket.status)}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;